/**
 * Locks Cache Service
 * Manages offline caching and real-time sync of locks data
 */

import { subscribeToCollection, getDocuments } from '../db_calls';
import { getCollectionPath } from '../utils/dbPaths';
import { useLocks } from '../stores';
import { isOnline } from './networkStatus';

// localStorage caching removed - using Firestore persistent cache only

/**
 * Initialize locks cache system
 * Sets up real-time listener and loads from cache if offline
 * @returns {Function} Unsubscribe function
 */
export const initializeLocksCache = () => {

  // Firestore persistent cache handles offline data automatically

  // Set up real-time subscription with tenant/store path
  const collectionPath = getCollectionPath('locks');
  console.log('🔒 Initializing locks cache for path:', collectionPath);
  const unsubscribe = subscribeToCollection(
    collectionPath,
    (locks, error) => {
      // Handle error case (callback is called with ([], error))
      if (error) {
        console.error('❌ Error in locks subscription:', error);
        return;
      }

      // Handle success case (callback is called with (documents))
      // If locks is an array and error is undefined, we have data
      if (Array.isArray(locks)) {
        console.log('🔒 Locks subscription received:', locks.length, 'locks');
        // Sort by tagNumber if it exists, otherwise by name
        const sortedLocks = [...locks].sort((a, b) => {
          const aTag = a.tagNumber !== undefined ? a.tagNumber : (a.name ? parseInt(a.name) || 0 : 0);
          const bTag = b.tagNumber !== undefined ? b.tagNumber : (b.name ? parseInt(b.name) || 0 : 0);
          return aTag - bTag;
        });
        
        // Update Zustand store
        const setLocks = useLocks.getState().setLocks;
        setLocks(sortedLocks);
        console.log('🔒 Zustand store updated with', sortedLocks.length, 'locks');
      } else {
        console.warn('🔒 Unexpected locks data format:', locks);
      }
    },
    {
      // Removed orderBy - will sort manually to handle missing tagNumber fields
    }
  );

  return unsubscribe;
};

/**
 * Get all locks from cache (synchronous)
 * @returns {Array} Array of lock objects
 */
export const getLocksFromCache = () => {
  try {
    const locks = useLocks.getState().locks;
    return locks;
  } catch (error) {
    console.error('❌ Error reading locks from cache:', error);
    return [];
  }
};

/**
 * Get a single lock by tag number from cache
 * @param {number} tagNumber - Lock tag number
 * @returns {Object|null} Lock object or null if not found
 */
export const getLockFromCache = (tagNumber) => {
  const locks = getLocksFromCache();
  const lock = locks.find(l => l.tagNumber === tagNumber);
  
  
  return lock || null;
};

/**
 * Get combination for a specific lock tag
 * @param {number} tagNumber - Lock tag number
 * @returns {string} Combination or empty string if not found
 */
export const getCombinationFromCache = (tagNumber) => {
  const lock = getLockFromCache(tagNumber);
  return lock?.combination || '';
};

// localStorage caching functions removed - using Firestore persistent cache only

/**
 * Force refresh locks from server
 * @returns {Promise<Array>} Array of locks
 */
export const refreshLocksCache = async () => {
  
  try {
    const collectionPath = getCollectionPath('locks');
    const locks = await getDocuments(collectionPath, {
      orderBy: {
        field: 'tagNumber',
        direction: 'asc',
      },
    });
    
    // Update Zustand store
    useLocks.getState().setLocks(locks);
    return locks;
  } catch (error) {
    console.error('❌ Error refreshing locks cache:', error);
    // Return cached data as fallback
    return getLocksFromCache();
  }
};

/**
 * Update a single lock in cache
 * @param {number} tagNumber - Lock tag number
 * @param {Object} updates - Fields to update
 */
export const updateLockInCache = (tagNumber, updates) => {
  try {
    const locks = getLocksFromCache();
    const index = locks.findIndex(l => l.tagNumber === tagNumber);
    
    if (index !== -1) {
      const updatedLocks = [...locks];
      updatedLocks[index] = { ...updatedLocks[index], ...updates };
      useLocks.getState().setLocks(updatedLocks);
    }
  } catch (error) {
    console.error('❌ Error updating lock in cache:', error);
  }
};

/**
 * Add a new lock to cache
 * @param {Object} lock - Lock object
 */
export const addLockToCache = (lock) => {
  try {
    const locks = getLocksFromCache();
    
    // Check if lock already exists
    const existingIndex = locks.findIndex(l => l.tagNumber === lock.tagNumber);
    
    if (existingIndex !== -1) {
      // Update existing lock
      locks[existingIndex] = lock;
    } else {
      // Add new lock
      locks.push(lock);
    }
    
    // Sort by tag number
    locks.sort((a, b) => (a.tagNumber || 0) - (b.tagNumber || 0));
    
    // Update Zustand store
    useLocks.getState().setLocks(locks);
  } catch (error) {
    console.error('❌ Error adding lock to cache:', error);
  }
};

/**
 * Remove a lock from cache
 * @param {number} tagNumber - Lock tag number
 */
export const removeLockFromCache = (tagNumber) => {
  try {
    const locks = getLocksFromCache();
    const filtered = locks.filter(l => l.tagNumber !== tagNumber);
    
    if (filtered.length < locks.length) {
      useLocks.getState().setLocks(filtered);
    }
  } catch (error) {
    console.error('❌ Error removing lock from cache:', error);
  }
};

/**
 * Clear all locks from cache
 */
export const clearLocksCache = () => {
  try {
    useLocks.getState().clearLocks();
  } catch (error) {
    console.error('❌ Error clearing locks cache:', error);
  }
};

/**
 * Get cache statistics
 * @returns {Object} Cache stats
 */
export const getLocksCacheStats = () => {
  const locks = getLocksFromCache();
  
  return {
    count: locks.length,
    lastUpdated: null, // No longer tracking timestamp - using Firestore cache
    locksWithCombinations: locks.filter(l => l.combination).length,
    locksWithoutCombinations: locks.filter(l => !l.combination).length,
  };
};

